import { FileType } from "shared/utils/enum";
import { Icons } from "assets";
import classNames from "classnames";
import CustomInput from "shared/components/common/customInput";
import CustomTextEditor from "shared/components/common/customTextEditor";
import CustomFileUpload from "shared/components/common/customFileUpload";
import CustomDeleteButton from "shared/components/common/customDeleteButton";
import styles from "./style.module.scss";

export interface ImageItem {
  id: string;
  imageUrl?: string;
  imageLabel: string;
  notes: string;
  file?: File | null;
  hasImage: boolean;
}

interface ImageItemsListProps {
  imageItems: ImageItem[];
  errors?: Record<string, string>;
  onFileChange: (id: string, file: File | null, preview: string | null) => void;
  onImageLabelChange: (id: string, value: string) => void;
  onNotesChange: (id: string, value: string) => void;
  onAddMore: () => void;
  onDelete: (id: string) => void;
  onChangePhoto?: (id: string) => void;
  notesRequired?: boolean;
  notesLabel?: string;
  containerClassName?: string;
  showShadow?: boolean;
  showPadding?: boolean;
  smallImageUpload?: boolean;
  accept?: {
    "application/pdf"?: string[];
    "application/msword"?: string[];
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document"?: string[];
    "image/*"?: string[];
    "video/*"?: string[];
  };
}

const ImageItemsList = ({
  imageItems,
  errors = {},
  onFileChange,
  onImageLabelChange,
  onNotesChange,
  onAddMore,
  onDelete,
  onChangePhoto,
  notesRequired = false,
  notesLabel = "Additional Notes",
  containerClassName,
  showShadow = true,
  showPadding = true,
  smallImageUpload = false,
  accept = {
    "application/pdf": [],
    "image/*": []
  },
}: ImageItemsListProps) => {
  return (
    <div
      className={classNames(
        styles.imageContainer,
        !showShadow && styles.noShadow,
        !showPadding && styles.noPadding,
        containerClassName,
        imageItems.length > 2
          ? styles.scrollableContainer
          : "flex flex-col gap-3",
      )}
    >
      {imageItems.map((item) => (
        <div key={item.id} className={classNames(styles.border, "relative")}>
          <div
            className={classNames(
              styles.itemContent,
              "grid grid-cols-2",
              imageItems.length > 1 && styles.withDelete,
            )}
          >
            <div
              className={classNames(
                styles.imageWrapper,
                smallImageUpload && styles.smallImageWrapper,
                smallImageUpload && "smallImageWrapper80",
              )}
            >
              <div className={styles.uploadWrapper}>
                <CustomFileUpload
                  accept={accept}
                  onFileChange={(file, preview) =>
                    onFileChange(item.id, file, preview)
                  }
                  initialPreview={
                    item.hasImage && item.imageUrl ? item.imageUrl : undefined
                  }
                  previewType={
                    item.hasImage && item.imageUrl ? FileType.IMAGE : undefined
                  }
                  showChangePhotoButton={item.hasImage && !!item.imageUrl}
                  onChangePhoto={
                    onChangePhoto ? () => onChangePhoto(item.id) : undefined
                  }
                  size={
                    smallImageUpload ? { width: 80, height: 80 } : undefined
                  }
                />
                {errors[`image-${item.id}`] && (
                  <span className={styles.errorText}>
                    {errors[`image-${item.id}`]}
                  </span>
                )}
              </div>
            </div>

            <div className="flex flex-col gap-2 flex-1 min-w-0">
              <div className="">
                <CustomInput
                  label="File Label"
                  required={true}
                  placeholder="Enter file label"
                  name={`imageLabel-${item.id}`}
                  value={item.imageLabel}
                  onChange={(e: any) =>
                    onImageLabelChange(item.id, e.target.value)
                  }
                  customLabelStyle={styles.labelStyle}
                  customInputContainer={styles.customInputStyle}
                  error={errors[`imageLabel-${item.id}`]}
                />
              </div>

              <div className={styles.notesSection}>
                {!notesRequired ? (
                  <>
                    <div className={styles.notesHeader}>
                      <h6 className={styles.notesTitle}>{notesLabel}</h6>
                      {errors[`notes-${item.id}`] && (
                        <span className={styles.errorText}>
                          {errors[`notes-${item.id}`]}
                        </span>
                      )}
                    </div>
                    <CustomTextEditor
                      value={item.notes}
                      onChange={(value) => onNotesChange(item.id, value)}
                      placeholder="Type your message here..."
                      customToolbarStyle={styles.customToolbarStyle}
                      customContainerStyle={styles.customContainerStyle}
                    />
                  </>
                ) : (
                  <>
                    <CustomTextEditor
                      label={notesLabel}
                      required={notesRequired}
                      value={item.notes}
                      onChange={(value) => onNotesChange(item.id, value)}
                      placeholder="Type your message here..."
                      customToolbarStyle={styles.customToolbarStyle}
                      customContainerStyle={styles.customContainerStyle}
                    />
                    {errors[`notes-${item.id}`] && (
                      <div className="text-red-500 text-sm mt-1">
                        {errors[`notes-${item.id}`]}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {imageItems.length > 1 && (
              <CustomDeleteButton
                onClick={() => onDelete(item.id)}
                className={styles.iconContainer}
                ariaLabel="Delete this item"
              />
            )}
          </div>
        </div>
      ))}

      <div className={styles.uploadContainer} onClick={onAddMore}>
        <div className={classNames(styles.uploadArea, "cursor-pointer")}>
          <div className="flex items-center justify-center gap-2 p-4">
            <Icons.ImgUpload className={styles.uploadIconPlaceholder} />
            <p className="">Add More</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageItemsList;
